<script setup>
import { reactive, computed, watch, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

const sourceData = computed(
  () => $stores.appData?.state?.data?.amountLimits || {}
);

// Available countries
const countries = {
  BJ: 'Bénin',
  BF: 'Burkina Faso',
  CI: "Côte d'Ivoire",
  GA: 'Gabon',
  ML: 'Mali',
  NE: 'Niger',
  SN: 'Sénégal',
  TG: 'Togo'
};

// Selected country
const selectedCountry = reactive({ code: 'BJ' });

// Limit types
const limitTypes = ['minAmounts', 'maxAmounts', 'dailyLimits', 'monthlyLimits'];

// Fields for each type
const limitFields = {
  minAmounts: {
    forTransaction: 'Transaction générale',
    depositWallet: 'Recharge wallet',
    withdrawalWallet: 'Retrait wallet',
    depositXbet: 'Recharge 1xBet',
    withdrawXbet: 'Retrait 1xBet',
    depositBetwinner: 'Recharge Betwinner',
    withdrawBetwinner: 'Retrait Betwinner',
    depositOneWin: 'Recharge 1Win',
    withdrawOneWin: 'Retrait 1Win',
    buyUsdt: 'Achat USDT',
    sellUsdt: 'Vente USDT',
    depositEightStarz: 'Recharge 8 Starz',
    withdrawEightStarz: 'Retrait 8 Starz',
    depositMelbet: 'Recharge Melbet',
    withdrawMelbet: 'Retrait Melbet'
  },
  maxAmounts: {
    forTransaction: 'Transaction générale',
    depositWallet: 'Recharge wallet',
    withdrawalWallet: 'Retrait wallet',
    depositXbet: 'Recharge 1xBet',
    withdrawXbet: 'Retrait 1xBet',
    depositBetwinner: 'Recharge Betwinner',
    withdrawBetwinner: 'Retrait Betwinner',
    depositOneWin: 'Recharge 1Win',
    withdrawOneWin: 'Retrait 1Win',
    buyUsdt: 'Achat USDT',
    sellUsdt: 'Vente USDT',
    depositEightStarz: 'Recharge 8 Starz',
    withdrawEightStarz: 'Retrait 8 Starz',
    depositMelbet: 'Recharge Melbet',
    withdrawMelbet: 'Retrait Melbet'
  },
  dailyLimits: {
    forTransaction: 'Limite transaction',
    depositWallet: 'Limite dépôt',
    withdrawalWallet: 'Limite retrait',
    depositXbet: 'Limite dépôt 1xBet',
    withdrawXbet: 'Limite retrait 1xBet',
    depositBetwinner: 'Limite dépôt Betwinner',
    withdrawBetwinner: 'Limite retrait Betwinner',
    depositOneWin: 'Limite dépôt 1Win',
    withdrawOneWin: 'Limite retrait 1Win',
    buyUsdt: 'Limite achat USDT',
    sellUsdt: 'Limite vente USDT',
    depositEightStarz: 'Limite dépôt 8 Starz',
    withdrawEightStarz: 'Limite retrait 8 Starz',
    depositMelbet: 'Limite dépôt Melbet',
    withdrawMelbet: 'Limite retrait Melbet'
  },
  monthlyLimits: {
    forTransaction: 'Limite transaction',
    depositWallet: 'Limite dépôt',
    withdrawalWallet: 'Limite retrait',
    depositXbet: 'Limite dépôt 1xBet',
    withdrawXbet: 'Limite retrait 1xBet',
    depositBetwinner: 'Limite dépôt Betwinner',
    withdrawBetwinner: 'Limite retrait Betwinner',
    depositOneWin: 'Limite dépôt 1Win',
    withdrawOneWin: 'Limite retrait 1Win',
    buyUsdt: 'Limite achat USDT',
    sellUsdt: 'Limite vente USDT',
    depositEightStarz: 'Limite dépôt 8 Starz',
    withdrawEightStarz: 'Limite retrait 8 Starz',
    depositMelbet: 'Limite dépôt Melbet',
    withdrawMelbet: 'Limite retrait Melbet'
  }
};

import { platform } from '@core/platform';

// Section names
const sectionNames = {
  minAmounts: 'Montants minimums',
  maxAmounts: 'Montants maximums',
  dailyLimits: 'Limites journalières',
  monthlyLimits: 'Limites mensuelles'
};

const filteredLimitFields = computed(() => {
  const filtered = {};
  Object.keys(limitFields).forEach((type) => {
    filtered[type] = Object.fromEntries(
      Object.entries(limitFields[type]).filter(([field]) => {
        const isWalletField = field.toLowerCase().includes('wallet');
        return !isWalletField || platform.hasWallet;
      })
    );
  });
  return filtered;
});

// Form data for each country and type
const formData = reactive({});

// Initialize form data
watch(
  sourceData,
  (data) => {
    if (!data) return;

    Object.keys(countries).forEach((countryCode) => {
      if (!formData[countryCode]) {
        formData[countryCode] = {};
      }

      limitTypes.forEach((type) => {
        if (!formData[countryCode][type]) {
          formData[countryCode][type] = {};
        }

        Object.keys(limitFields[type]).forEach((field) => {
          formData[countryCode][type][field] =
            data?.[countryCode]?.[type]?.[field] || 0;
        });
      });
    });
  },
  { immediate: true }
);

// Check if current country section has changes
const countrySectionChanged = (countryCode, type) => {
  return Object.keys(formData[countryCode]?.[type] || {}).some(
    (key) =>
      formData[countryCode][type][key] !==
      sourceData.value?.[countryCode]?.[type]?.[key]
  );
};

const hasChanges = computed(() => {
  return Object.keys(countries).some((countryCode) =>
    limitTypes.some((type) => countrySectionChanged(countryCode, type))
  );
});

function save() {
  if (!hasChanges.value) return;

  const payload = { amountLimits: {} };

  Object.keys(countries).forEach((countryCode) => {
    if (!payload.amountLimits[countryCode]) {
      payload.amountLimits[countryCode] = {};
    }

    limitTypes.forEach((type) => {
      if (!payload.amountLimits[countryCode][type]) {
        payload.amountLimits[countryCode][type] = {};
      }

      Object.keys(limitFields[type]).forEach((field) => {
        if (
          formData[countryCode][type][field] !==
          sourceData.value?.[countryCode]?.[type]?.[field]
        ) {
          payload.amountLimits[countryCode][type][field] =
            formData[countryCode][type][field];
        }
      });
    });
  });

  $bus.emit($constants.EVENTS.APP_DATA.UPDATE_LIMITS, payload);
}

function resetCountrySection(countryCode, type) {
  Object.keys(formData[countryCode]?.[type] || {}).forEach((key) => {
    formData[countryCode][type][key] =
      sourceData.value?.[countryCode]?.[type]?.[key] || 0;
  });
}

function resetCurrentCountry() {
  limitTypes.forEach((type) => {
    resetCountrySection(selectedCountry.code, type);
  });
}

function resetAll() {
  Object.keys(countries).forEach((countryCode) => {
    limitTypes.forEach((type) => {
      resetCountrySection(countryCode, type);
    });
  });
}
</script>

<template>
  <div class="space-y-8">
    <!-- HEADER -->
    <div class="flex justify-between items-center sticky top-0 py-2 z-2">
      <h2 class="text-lg font-semibold">Limites de montant</h2>
      <div class="flex gap-4" v-show="hasChanges">
        <button
          class="px-6 py-2 rounded-xl bg-gray-300 text-gray-700"
          @click="resetAll"
        >
          Annuler tout
        </button>
        <button
          class="px-6 py-2 rounded-xl bg-primary-600 text-white"
          @click="save"
        >
          Sauvegarder
        </button>
      </div>
    </div>

    <!-- COUNTRY SELECTOR -->
    <div class="bg-slate-100 rounded-3xl p-6">
      <h3 class="text-md font-semibold text-primary-600 mb-4">
        Sélectionner un pays
      </h3>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="(name, code) in countries"
          :key="code"
          class="px-4 py-2 rounded-lg font-medium transition-all"
          :class="
            selectedCountry.code === code
              ? 'bg-primary-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          "
          @click="selectedCountry.code = code"
        >
          {{ name }}
        </button>
      </div>
    </div>

    <!-- LIMITS SECTIONS FOR SELECTED COUNTRY -->
    <div
      v-for="type in limitTypes"
      :key="type"
      class="bg-slate-100 rounded-3xl p-6 space-y-4 relative"
    >
      <div class="flex justify-between items-center">
        <h3 class="text-md font-semibold text-primary-600">
          {{ sectionNames[type] }} - {{ countries[selectedCountry.code] }}
        </h3>
        <button
          v-if="countrySectionChanged(selectedCountry.code, type)"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetCountrySection(selectedCountry.code, type)"
        >
          Réinitialiser
        </button>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div v-for="(label, field) in filteredLimitFields[type]" :key="field">
          <label class="block mb-1 text-sm">{{ label }}</label>
          <input
            type="number"
            class="inputComponent"
            v-model.number="formData[selectedCountry.code][type][field]"
          />
        </div>
      </div>
    </div>
  </div>
</template>
